// Per-person preferences (GET and PATCH /api/me/preferences): pinned projects, folded nav groups, the pet, the
// parking lot's colours. The server is the source of truth so they follow a person across devices; this browser
// keeps a copy only as an offline cache, and settings saved here before the server store existed are carried up once.
import { useSyncExternalStore } from 'react';
import type { Preferences } from '@jabberr/core';
import { api } from './api.js';

const EVENT = 'jabberr:preferences';
let userId: string | null = null;
let current: Preferences = {};
/** Bumped by every load and reset: an answer from an older load (or another account's) is dropped. */
let generation = 0;
/** Keys this person changed since the current load began: the server's older answer never replaces them. */
let changedSinceLoad = new Set<keyof Preferences>();

const cacheKey = (uid: string) => `jabberr:prefs:${uid}`;

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeCache(): void {
  if (!userId) return;
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(current));
  } catch {
    /* storage blocked: the server still has them */
  }
}

function emit(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
}

/** What this browser held before preferences moved to the server (left nav and pet keys), for a one-time carry-up. */
function legacy(uid: string): Preferences {
  const out: Preferences = {};
  const pinned = readJson<unknown>(`jabberr:nav:pinned:${uid}`);
  if (Array.isArray(pinned)) out.pinnedProjects = pinned.filter((x): x is string => typeof x === 'string');
  const collapsed = readJson<unknown>(`jabberr:nav:collapsed:${uid}`);
  if (Array.isArray(collapsed)) out.collapsedNavGroups = collapsed.filter((x) => ['clients', 'products', 'platforms', 'just_me'].includes(x as string)) as Preferences['collapsedNavGroups'];
  try {
    const pet = localStorage.getItem('jabberr.pet.visible');
    if (pet === '1' || pet === '0') out.petVisible = pet === '1';
    const colour = localStorage.getItem('jabberr:parking:colourBy');
    if (colour === 'project' || colour === 'column') out.parkingColourBy = colour;
  } catch {
    /* nothing to carry */
  }
  return out;
}

/** Signs the store in for a person: the cached copy at once, then the server's, carrying up anything only this browser had. */
export async function loadPreferences(uid: string): Promise<void> {
  const mine = ++generation;
  userId = uid;
  changedSinceLoad = new Set();
  current = readJson<Preferences>(cacheKey(uid)) ?? legacy(uid);
  emit();
  try {
    const { preferences } = await api.preferences.get();
    // A newer load (this person again, or someone else signed in) owns the store now; this answer is stale.
    if (mine !== generation || userId !== uid) return;
    const local = legacy(uid);
    const missing = Object.fromEntries(Object.entries(local).filter(([k]) => !(k in preferences))) as Preferences;
    // What the person changed while the answer was on its way is newer than the answer, and its PATCH is already sent.
    const newer = Object.fromEntries([...changedSinceLoad].filter((k) => k in current).map((k) => [k, current[k]])) as Preferences;
    current = { ...preferences, ...missing, ...newer };
    writeCache();
    emit();
    if (Object.keys(missing).length > 0) await api.preferences.update(missing).catch(() => undefined);
  } catch {
    /* offline or signed out: the cache stands */
  }
}

export function preferences(): Preferences {
  return current;
}

/** Changes some preferences now (and in the cache), then saves them for every device. */
export function setPreferences(patch: Preferences): void {
  for (const k of Object.keys(patch) as Array<keyof Preferences>) changedSinceLoad.add(k);
  current = { ...current, ...patch };
  writeCache();
  emit();
  if (userId) void api.preferences.update(patch).catch(() => undefined);
}

function subscribe(fn: () => void): () => void {
  window.addEventListener(EVENT, fn);
  return () => window.removeEventListener(EVENT, fn);
}

export function usePreference<K extends keyof Preferences>(key: K): Preferences[K] {
  return useSyncExternalStore(subscribe, () => current[key], () => current[key]);
}

/** Tests only. */
export function _resetPreferences(): void {
  generation += 1;
  userId = null;
  current = {};
  changedSinceLoad = new Set();
}
